import { useRef, useState } from "preact/hooks";

import { useModalA11y } from "../hooks/useModalA11y";
import type { CalendarMutationResult } from "../hooks/useSchedule";
import {
  CALENDAR_GENERIC_ERROR,
  RESCHEDULE_ALREADY_DONE_ERROR,
  rescheduleReassuranceCopy,
  rescheduleSheetTitle,
} from "../lib/calendarCopy";
import { toISODate, todayLocal } from "../lib/calendarDates";
import { monthDayLabel } from "../lib/recordsCopy";
import type { ScheduleOccurrence } from "../types";

type Step = "actions" | "date-picker" | "conflict";

export interface CalendarRescheduleSheetProps {
  occurrence: ScheduleOccurrence | null;
  onCancel: () => void;
  onMoveCadence: (
    occurrence: ScheduleOccurrence,
    newDateIso: string,
  ) => Promise<CalendarMutationResult>;
  onSkipCadence: (occurrence: ScheduleOccurrence) => Promise<CalendarMutationResult>;
  onMoveOneOff: (
    occurrence: ScheduleOccurrence,
    newDateIso: string,
  ) => Promise<CalendarMutationResult>;
}

/**
 * §2.8 — the reschedule/skip action sheet, admin/manager/api only (a
 * member's chip tap never opens this — see `views/CalendarScreen.tsx`'s
 * `handleTapOccurrence`). Reuses `.assign-sheet` chrome verbatim
 * (`AssigneePickerSheet`'s own precedent). Immediate action for Skip (no
 * second sheet, per §0's "immediate + Undo" decision); "Move to another
 * day" opens a second small step with a native `<input type="date">`
 * (`min` = today — never move into the past). The 409 completion-wins
 * case (§2.8) replaces the button row with an inline `role="alert"` line;
 * Cancel is the only way out from there.
 */
export function CalendarRescheduleSheet({
  occurrence,
  onCancel,
  onMoveCadence,
  onSkipCadence,
  onMoveOneOff,
}: CalendarRescheduleSheetProps) {
  const [step, setStep] = useState<Step>("actions");
  const [newDate, setNewDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [genericError, setGenericError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const open = occurrence !== null;

  // Synchronous reset on the closed->open transition (same race-avoidance
  // discipline `AddHealthNoteSheet.tsx` documents — never inside a
  // post-commit effect).
  const wasOpenRef = useRef(open);
  if (open && !wasOpenRef.current) {
    setStep("actions");
    setNewDate("");
    setGenericError(false);
  }
  wasOpenRef.current = open;

  // Sprint-011 C1 (TD-012, SA plan §3 clause 3) — every "Cancel" affordance
  // + the backdrop below route through the hook's returned `close`, not
  // `onCancel` directly.
  const { close } = useModalA11y(containerRef, { active: open, onClose: onCancel });

  if (!occurrence) return null;

  const isCadence = occurrence.kind === "cadence";
  const todayIso = toISODate(todayLocal());

  async function handleMove() {
    if (!newDate || !occurrence) return;
    setBusy(true);
    setGenericError(false);
    const result = isCadence
      ? await onMoveCadence(occurrence, newDate)
      : await onMoveOneOff(occurrence, newDate);
    setBusy(false);
    if (result.ok) {
      onCancel();
      return;
    }
    if (result.conflict) {
      setStep("conflict");
      return;
    }
    setGenericError(true);
  }

  async function handleSkip() {
    if (!occurrence) return;
    setBusy(true);
    setGenericError(false);
    const result = await onSkipCadence(occurrence);
    setBusy(false);
    if (result.ok) {
      onCancel();
      return;
    }
    if (result.conflict) {
      setStep("conflict");
      return;
    }
    setGenericError(true);
  }

  return (
    <>
      <div class="assign-sheet-backdrop is-open" onClick={close} />
      <div
        ref={containerRef}
        class="assign-sheet is-open"
        role="dialog"
        aria-modal="true"
        aria-labelledby="calendar-reschedule-title"
      >
        <div class="assign-sheet-handle" aria-hidden="true" />
        <h2 class="assign-sheet-title" id="calendar-reschedule-title">
          {rescheduleSheetTitle(occurrence.title)}
        </h2>

        {step === "conflict" ? (
          <>
            <p class="error-hint" role="alert">
              {RESCHEDULE_ALREADY_DONE_ERROR}
            </p>
            <button type="button" class="secondary-btn" onClick={close}>
              Cancel
            </button>
          </>
        ) : step === "date-picker" ? (
          <>
            <label class="admin-field-label" for="calendar-reschedule-date">
              Move to which day?
            </label>
            <input
              id="calendar-reschedule-date"
              type="date"
              class="pick-input"
              min={todayIso}
              value={newDate}
              onInput={(e) => setNewDate((e.target as HTMLInputElement).value)}
            />
            {genericError ? (
              <p class="error-hint" role="alert">
                {CALENDAR_GENERIC_ERROR}
              </p>
            ) : null}
            <button
              type="button"
              class="primary-btn"
              style={{ marginTop: "14px" }}
              disabled={!newDate || busy}
              onClick={() => void handleMove()}
            >
              Move it
            </button>
            <button
              type="button"
              class="secondary-btn"
              style={{ marginTop: "10px" }}
              onClick={close}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            {isCadence && occurrence.phrase ? (
              <p class="calendar-reschedule-reassurance">
                {rescheduleReassuranceCopy(occurrence.phrase, monthDayLabel(occurrence.date))}
              </p>
            ) : null}
            {genericError ? (
              <p class="error-hint" role="alert">
                {CALENDAR_GENERIC_ERROR}
              </p>
            ) : null}
            <button type="button" class="primary-btn" onClick={() => setStep("date-picker")}>
              Move to another day
            </button>
            {isCadence ? (
              <button
                type="button"
                class="secondary-btn"
                style={{ marginTop: "10px" }}
                disabled={busy}
                onClick={() => void handleSkip()}
              >
                Skip this one
              </button>
            ) : null}
            <button
              type="button"
              class="secondary-btn"
              style={{ marginTop: "10px" }}
              onClick={close}
            >
              Cancel
            </button>
          </>
        )}
      </div>
    </>
  );
}
